// DataIntegrityPage.js - Pagina Admin per Report Integrità Dati

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { AlertCircle, CheckCircle, RefreshCw, Database, AlertTriangle, Info, ChevronDown, ChevronUp, FileText } from 'lucide-react';
import api from '../lib/api';

const DataIntegrityPage = () => {
  const [report, setReport] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedIssues, setExpandedIssues] = useState({});

  // Carica summary all'avvio
  useEffect(() => {
    loadSummary();
  }, []);

  const loadSummary = async () => {
    try {
      const response = await api.get('/debug/data-integrity/summary');
      setSummary(response.data);
    } catch (err) {
      console.error('Errore caricamento summary:', err);
    }
  };

  const loadFullReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/debug/data-integrity');
      setReport(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Errore nel caricamento del report');
    } finally {
      setLoading(false);
    }
  };

  const toggleIssueExpand = (index) => {
    setExpandedIssues(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'CRITICAL': return 'bg-red-500';
      case 'HIGH': return 'bg-orange-500';
      case 'MEDIUM': return 'bg-yellow-500';
      case 'WARNING': return 'bg-blue-500';
      default: return 'bg-gray-500';
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'CRITICAL':
      case 'HIGH':
        return <AlertCircle className="h-4 w-4" />;
      case 'MEDIUM':
      case 'WARNING':
        return <AlertTriangle className="h-4 w-4" />;
      default:
        return <Info className="h-4 w-4" />;
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return 'N/A';
    return new Date(isoString).toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="p-6 space-y-6" data-testid="data-integrity-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" data-testid="page-title">
            Report Integrità Dati
          </h1>
          <p className="text-gray-500 mt-1">
            Verifica la consistenza dei riferimenti nel database
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={loadSummary}
            disabled={loading}
            data-testid="refresh-summary-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Aggiorna Summary
          </Button>
          <Button
            onClick={loadFullReport}
            disabled={loading}
            data-testid="generate-report-btn"
          >
            <FileText className="h-4 w-4 mr-2" />
            {loading ? 'Generazione...' : 'Genera Report Completo'}
          </Button>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3" data-testid="error-alert">
          <AlertCircle className="h-5 w-5 text-red-500 mt-0.5" />
          <div>
            <p className="text-red-700 font-medium">Errore</p>
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Status Card */}
          <Card data-testid="status-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Stato Sistema</p>
                  <p className="text-2xl font-bold mt-1">
                    {summary.has_issues ? (
                      <span className="text-yellow-600">Attenzione</span>
                    ) : (
                      <span className="text-green-600">OK</span>
                    )}
                  </p>
                </div>
                {summary.has_issues ? (
                  <AlertTriangle className="h-10 w-10 text-yellow-500" />
                ) : (
                  <CheckCircle className="h-10 w-10 text-green-500" />
                )}
              </div>
            </CardContent>
          </Card>

          {/* Collection Counts */}
          <Card data-testid="collections-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Totale Record</p>
                  <p className="text-2xl font-bold mt-1">
                    {Object.values(summary.collection_counts).reduce((a, b) => a + b, 0)}
                  </p>
                </div>
                <Database className="h-10 w-10 text-blue-500" />
              </div>
            </CardContent>
          </Card>

          {/* Projects */}
          <Card data-testid="projects-card">
            <CardContent className="pt-6">
              <div className="space-y-2">
                <p className="text-sm text-gray-500">Conteggio Collezioni</p>
                <div className="grid grid-cols-2 gap-1 text-sm">
                  <span className="text-gray-600">Lead:</span>
                  <span className="font-medium">{summary.collection_counts.leads}</span>
                  <span className="text-gray-600">Preventivi:</span>
                  <span className="font-medium">{summary.collection_counts.estimates}</span>
                  <span className="text-gray-600">Progetti:</span>
                  <span className="font-medium">{summary.collection_counts.projects}</span>
                  <span className="text-gray-600">Contratti:</span>
                  <span className="font-medium">{summary.collection_counts.contracts}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Quick Issues */}
          <Card data-testid="quick-issues-card">
            <CardContent className="pt-6">
              <div className="space-y-2">
                <p className="text-sm text-gray-500">Controlli Rapidi</p>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Progetti senza lead:</span>
                    <Badge variant={summary.quick_issues.projects_without_lead > 0 ? 'destructive' : 'secondary'}>
                      {summary.quick_issues.projects_without_lead}
                    </Badge>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Preventivi orfani:</span>
                    <Badge variant={summary.quick_issues.orphan_validated_estimates > 0 ? 'warning' : 'secondary'}>
                      {summary.quick_issues.orphan_validated_estimates}
                    </Badge>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Contratti orfani:</span>
                    <Badge variant={summary.quick_issues.orphan_approved_contracts > 0 ? 'warning' : 'secondary'}>
                      {summary.quick_issues.orphan_approved_contracts}
                    </Badge>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Full Report */}
      {report && (
        <Card data-testid="full-report-card">
          <CardHeader>
            <div className="flex justify-between items-start">
              <div>
                <CardTitle>Report Completo</CardTitle>
                <CardDescription>
                  Generato il {formatDate(report.generated_at)} da {report.generated_by}
                </CardDescription>
              </div>
              <div className="flex gap-2">
                <Badge variant="outline" className="text-red-600 border-red-300">
                  Critici: {report.summary.critical_issues}
                </Badge>
                <Badge variant="outline" className="text-yellow-600 border-yellow-300">
                  Warning: {report.summary.warning_issues}
                </Badge>
                <Badge variant="outline">
                  Totale: {report.summary.total_issues}
                </Badge>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Collections Scanned */}
            <div className="mb-6">
              <p className="text-sm text-gray-500 mb-2">Collezioni analizzate:</p>
              <div className="flex flex-wrap gap-2">
                {report.collections_scanned.map((coll) => (
                  <Badge key={coll} variant="secondary">{coll}</Badge>
                ))}
              </div>
            </div>

            {/* Issues List */}
            {report.issues.length === 0 ? (
              <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center" data-testid="no-issues-message">
                <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-3" />
                <p className="text-green-700 font-medium text-lg">Nessun problema rilevato</p>
                <p className="text-green-600 text-sm mt-1">
                  Tutti i riferimenti nel database sono validi e consistenti.
                </p>
              </div>
            ) : (
              <div className="space-y-3" data-testid="issues-list">
                {report.issues.map((issue, index) => (
                  <div
                    key={issue.id || `issue-${issue.type}-${index}`}
                    className="border rounded-lg overflow-hidden"
                    data-testid={`issue-item-${index}`}
                  >
                    {/* Issue Header */}
                    <div
                      className="flex items-center justify-between p-4 bg-gray-50 cursor-pointer hover:bg-gray-100"
                      onClick={() => toggleIssueExpand(index)}
                    >
                      <div className="flex items-center gap-3">
                        <Badge className={`${getSeverityColor(issue.severity)} text-white`}>
                          {getSeverityIcon(issue.severity)}
                          <span className="ml-1">{issue.severity}</span>
                        </Badge>
                        <span className="font-medium">{issue.entity_type.toUpperCase()}</span>
                        <span className="text-gray-600">{issue.entity_number}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">{issue.issue_type}</Badge>
                        {expandedIssues[index] ? (
                          <ChevronUp className="h-4 w-4 text-gray-500" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-gray-500" />
                        )}
                      </div>
                    </div>

                    {/* Issue Details (Expanded) */}
                    {expandedIssues[index] && (
                      <div className="p-4 bg-white border-t space-y-3">
                        <div>
                          <p className="text-sm text-gray-500">Descrizione</p>
                          <p className="text-gray-700">{issue.description}</p>
                        </div>
                        {issue.field && (
                          <div>
                            <p className="text-sm text-gray-500">Campo</p>
                            <code className="text-sm bg-gray-100 px-2 py-1 rounded">{issue.field}</code>
                          </div>
                        )}
                        {issue.missing_id && (
                          <div>
                            <p className="text-sm text-gray-500">ID Mancante</p>
                            <code className="text-xs bg-red-50 text-red-700 px-2 py-1 rounded break-all">
                              {issue.missing_id}
                            </code>
                          </div>
                        )}
                        <div>
                          <p className="text-sm text-gray-500">Azione Consigliata</p>
                          <p className="text-gray-700 bg-yellow-50 p-2 rounded text-sm">
                            {issue.suggested_action}
                          </p>
                        </div>
                        <div className="pt-2 border-t">
                          <p className="text-xs text-gray-400">
                            Entity ID: {issue.entity_id}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Help Section */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Guida all'Interpretazione</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <p className="font-medium text-gray-700 mb-2">Livelli di Severità:</p>
              <ul className="space-y-1">
                <li className="flex items-center gap-2">
                  <Badge className="bg-red-500 text-white text-xs">CRITICAL</Badge>
                  <span className="text-gray-600">Riferimento mancante che blocca funzionalità</span>
                </li>
                <li className="flex items-center gap-2">
                  <Badge className="bg-orange-500 text-white text-xs">HIGH</Badge>
                  <span className="text-gray-600">Problema significativo da risolvere</span>
                </li>
                <li className="flex items-center gap-2">
                  <Badge className="bg-yellow-500 text-white text-xs">MEDIUM</Badge>
                  <span className="text-gray-600">Inconsistenza da verificare</span>
                </li>
                <li className="flex items-center gap-2">
                  <Badge className="bg-blue-500 text-white text-xs">WARNING</Badge>
                  <span className="text-gray-600">Anomalia potenziale</span>
                </li>
              </ul>
            </div>
            <div>
              <p className="font-medium text-gray-700 mb-2">Tipi di Problema:</p>
              <ul className="space-y-1 text-gray-600">
                <li><code className="bg-gray-100 px-1 rounded text-xs">missing_reference</code> - ID che punta a entità eliminata</li>
                <li><code className="bg-gray-100 px-1 rounded text-xs">orphan_converted</code> - Lead convertito senza progetto</li>
                <li><code className="bg-gray-100 px-1 rounded text-xs">orphan_validated</code> - Preventivo validato senza progetto</li>
                <li><code className="bg-gray-100 px-1 rounded text-xs">orphan_approved</code> - Contratto approvato senza progetto</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DataIntegrityPage;
